import * as React from 'react'
import type { ToolFolder, ToolFolderPayload } from '@/types/tool-taxonomy'

const STORAGE_KEY = 'tools-ui-folders-v1'

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `folder_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function loadFolders(): ToolFolder[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveFolders(folders: ToolFolder[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(folders))
}

export function useToolFolders() {
  const [folders, setFolders] = React.useState<ToolFolder[]>(loadFolders)
  const [loading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const refetch = React.useCallback(async () => {
    setError(null)
    setFolders(loadFolders())
  }, [])

  const createFolder = async (payload: ToolFolderPayload) => {
    const now = new Date().toISOString()
    const created: ToolFolder = {
      id: generateId(),
      name: payload.name.trim(),
      parent_id: payload.parent_id ?? null,
      ordering: payload.ordering ?? 0,
      created_at: now,
      updated_at: now,
      children_count: 0,
      tools_count: 0,
    }
    setFolders((prev) => {
      const next = [...prev, created]
      saveFolders(next)
      return next
    })
    return created
  }

  const updateFolder = async (folderId: string, payload: Partial<ToolFolderPayload>) => {
    const now = new Date().toISOString()
    let updated: ToolFolder | null = null
    setFolders((prev) => {
      const next = prev.map((folder) => {
        if (folder.id !== folderId) return folder
        updated = {
          ...folder,
          name: payload.name?.trim() || folder.name,
          parent_id: payload.parent_id === undefined ? folder.parent_id : payload.parent_id,
          ordering: payload.ordering ?? folder.ordering,
          updated_at: now,
        }
        return updated
      })
      saveFolders(next)
      return next
    })
    if (!updated) {
      throw new Error('Carpeta no encontrada')
    }
    return updated
  }

  const deleteFolder = async (folderId: string) => {
    setFolders((prev) => {
      const next = prev.filter((folder) => folder.id !== folderId)
      saveFolders(next)
      return next
    })
  }

  return {
    folders,
    loading,
    error,
    refetch,
    createFolder,
    updateFolder,
    deleteFolder,
  }
}
